import { useEffect, useState } from "react";
import apiService from '../ApiCallService';
import ErrorUtils from "src/utils/ErrorUtils";
import {
  PromotionComponentType,
  PromotionStatus,
  ValidateDependenciesRequest,
  ValidateDependenciesResponse,
  PromoteComponentRequest,
  PromoteComponentResponse,
  GetPromotionStatusResponse,
  GetPromotionHistoryResponse,
  PromotionWindowStatusResponse,
} from '../../models/promotion/PromotionTypes';

export default class PromotionService {

  getPromotionHistory(componentType: PromotionComponentType, componentId: string, trigger: string, limit: number = 10): [GetPromotionHistoryResponse | null, boolean, string] {
    const [result, setResult] = useState(null as GetPromotionHistoryResponse | null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    useEffect(() => {
      const fetchData = async () => {
        try {
          setLoading(true);
          const response = await apiService.getPromotionHistory(componentType, componentId, limit);
          const json = await response.json();
          setResult(json as GetPromotionHistoryResponse);
        } catch (ex) {
          setError(ErrorUtils.getMessage(ex));
        } finally {
          setLoading(false);
        }
      };

      if (trigger && componentId) {
        fetchData();
      }
    }, [trigger, componentId, componentType]);

    return [result, loading, error];
  }

  async validateDependencies(request: ValidateDependenciesRequest): Promise<ValidateDependenciesResponse> {
    const response = await apiService.validatePromotionDependencies(request);
    return response.json();
  }

  async promote(request: PromoteComponentRequest): Promise<PromoteComponentResponse> {
    const response = await apiService.promoteComponent(request);
    return response.json();
  }

  /**
   * Look up promotion status: by promotionId when we have one (started/resumed promotion),
   * falling back to componentId + componentType (latest promotion for the component).
   */
  async getStatus(params: { promotionId?: string; componentId?: string; componentType?: PromotionComponentType }): Promise<GetPromotionStatusResponse> {
    const response = await apiService.getPromotionStatus(
      params.promotionId
        ? { promotionId: params.promotionId }
        : { componentId: params.componentId, componentType: params.componentType }
    );
    return response.json();
  }

  async getPromotionHistoryByPeriod(accountingPeriod: string, limit: number, nextToken?: string): Promise<GetPromotionHistoryResponse> {
    const response = await apiService.getPromotionHistoryByPeriod(accountingPeriod, limit, nextToken);
    return response.json();
  }

  async getWindowStatus(): Promise<PromotionWindowStatusResponse> {
    const response = await apiService.getPromotionWindowStatus();
    return response.json();
  }
}
